import { useState } from 'react';
import { apiClient } from '../../lib/api';
import { Badge } from '../ui';
import { formatDateOnly } from '../../utils/dateUtils';
import { useToast } from '../../hooks/useToast';

export interface ReplyRowData {
  reply_key?: string;
  read?: boolean;
  name: string | null;
  linkedin_url: string | null;
  company: string | null;
  title: string | null;
  replied_at: string | null;
  reply_text: string | null;
  classification?: string;
  entity_name?: string | null;
  client_label?: string | null;
}

const CLASS_BADGE: Record<string, { variant: 'success' | 'neutral' | 'error'; label: string }> = {
  genuine: { variant: 'success', label: 'Genuine' },
  auto: { variant: 'neutral', label: 'OOO/Auto' },
  unsub: { variant: 'error', label: 'Unsub' },
  reaction: { variant: 'neutral', label: 'Reaction' }, // Badge has no 'info' variant; using neutral
  unknown: { variant: 'neutral', label: '—' },
};

export function ReplyRow({
  reply,
  showClient = false,
  onRead,
}: {
  reply: ReplyRowData;
  showClient?: boolean;
  onRead?: (replyKey: string) => void;
}) {
  const [read, setRead] = useState(!!reply.read);
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();

  const markRead = async () => {
    if (!reply.reply_key || read || busy) return;
    setBusy(true);
    try {
      await apiClient.markReplyRead(reply.reply_key);
      setRead(true);
      onRead?.(reply.reply_key);
    } catch {
      showToast('Failed to mark reply as read', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`text-sm rounded-md border p-3 ${read ? 'border-neutral-100 bg-neutral-50' : 'border-primary-100 bg-white'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {!read ? <span className="w-2 h-2 rounded-full bg-primary-600 shrink-0" aria-label="unread" /> : null}
          {reply.linkedin_url && /^https:\/\//i.test(reply.linkedin_url) ? (
            <a href={reply.linkedin_url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline">
              {reply.name ?? 'Unknown'}
            </a>
          ) : (
            <span className="font-medium text-neutral-900">{reply.name ?? 'Unknown'}</span>
          )}
          {reply.company || reply.title ? (
            <span className="text-neutral-500">{[reply.company, reply.title].filter(Boolean).join(' · ')}</span>
          ) : null}
          {showClient && (reply.entity_name || reply.client_label) ? (
            <Badge variant="neutral" size="sm">{reply.entity_name || reply.client_label}</Badge>
          ) : null}
          {reply.replied_at ? <span className="text-2xs text-neutral-400">{formatDateOnly(reply.replied_at)}</span> : null}
          {reply.classification && reply.classification !== 'unknown' && CLASS_BADGE[reply.classification] ? (
            <Badge variant={CLASS_BADGE[reply.classification].variant} size="sm">
              {CLASS_BADGE[reply.classification].label}
            </Badge>
          ) : null}
        </div>
        {reply.reply_key && !read ? (
          <button onClick={markRead} disabled={busy} className="text-2xs text-neutral-500 hover:text-primary-600 shrink-0 transition">
            {busy ? '…' : 'Mark read'}
          </button>
        ) : null}
      </div>
      {reply.reply_text ? <p className="mt-1 text-sm text-neutral-500 italic leading-relaxed">{reply.reply_text}</p> : null}
    </div>
  );
}
